/**
 * Date and time presentation for the dashboard.
 *
 * Every date the UI renders goes through this module, so locale and timezone
 * behaviour is decided in one place rather than at ~20 call sites. An ESLint
 * rule keeps `toLocaleDateString` / `toLocaleString` / `Intl.DateTimeFormat`
 * out of the rest of `apps/web/src`.
 *
 * `LOCALE` is `undefined`, meaning the viewer's browser locale — which is what
 * every call site did before this module existed. Pinning the dashboard to one
 * locale is a one-line change here.
 *
 * Every preset spells its options out in full. `toLocaleDateString()` with no
 * options fills in numeric year/month/day, and `toLocaleString()` adds numeric
 * hour/minute/second; writing those out changes nothing about the output and
 * means a reader does not have to remember which defaults apply to which call.
 *
 * Invalid input formats as the empty string, matching `formatAbsoluteTime`.
 */

export type DateInput = string | number | Date;

const LOCALE: string | undefined = undefined;

function toDate(value: DateInput): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function format(
  value: DateInput,
  options: Intl.DateTimeFormatOptions,
  locale: string | undefined = LOCALE,
): string {
  const date = toDate(value);
  return date ? date.toLocaleString(locale, options) : "";
}

/** "Sep 10, 2026" */
export function formatShortDate(value: DateInput): string {
  return format(value, { year: "numeric", month: "short", day: "numeric" });
}

/** "Sep 10" */
export function formatMonthDay(value: DateInput): string {
  return format(value, { month: "short", day: "numeric" });
}

/** "Sep 10, 02:12 PM" */
export function formatMonthDayTime(value: DateInput): string {
  return format(value, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "Thu, Sep 10" */
export function formatWeekdayMonthDay(value: DateInput): string {
  return format(value, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "Thu 10" — weekday plus day of month, for a chart axis. */
export function formatWeekdayDayOfMonth(value: DateInput): string {
  const date = toDate(value);
  if (!date) return "";
  return `${format(date, { weekday: "short" })} ${date.getDate()}`;
}

/** "02:12 PM" */
export function formatTimeOfDay(value: DateInput): string {
  return format(value, { hour: "2-digit", minute: "2-digit" });
}

/**
 * "14:12" — 24-hour clock, hour unpadded.
 *
 * Deliberately not `Intl`: this is a dense chart axis where every character
 * costs width, so it stays the same shape in every locale.
 */
export function formatShortTime24(value: DateInput): string {
  const date = toDate(value);
  if (!date) return "";
  return `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
}

/** "9/10/2026" */
export function formatNumericDate(value: DateInput): string {
  return format(value, { year: "numeric", month: "numeric", day: "numeric" });
}

/** "9/10/2026, 2:12:05 PM" */
export function formatNumericDateTime(value: DateInput): string {
  return format(value, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
}

/**
 * "Sep 10 14:12:05" — fixed-width log-line timestamp for the report table.
 *
 * The month name is pinned to `en-US` and the rest is assembled by hand so
 * every row is exactly the same width, which locale-dependent separators and
 * a 12-hour clock would not be.
 */
export function formatLogTimestamp(value: DateInput): string {
  const date = toDate(value);
  if (!date) return "";

  const month = format(date, { month: "short" }, "en-US");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${month} ${day} ${hours}:${minutes}:${seconds}`;
}
